import { useEffect, useId, useMemo, useRef, useState } from "react";
import { tv } from "tailwind-variants";

import type { ProgressChart } from "@/domains/progress/models/types";
import {
  buildProgressChartDescription,
  countSkippedCheckIns,
  formatProgressScore,
} from "@/domains/progress/utils/chart-copy";
import type {
  ProgressChartGeometry,
  ProgressChartIdealSpan,
  ProgressChartNode,
  ProgressChartValueLabelPlacement,
} from "@/domains/progress/utils/chart-geometry";
import {
  buildProgressChartGeometry,
  filterVisibleTicks,
  resolvePillPlacement,
  resolveValueLabelPlacements,
} from "@/domains/progress/utils/chart-geometry";

const NO_READINGS_MESSAGE = "No readings yet.";

const plotVariants = tv({
  slots: {
    dot: "block rounded-full bg-accent ring-surface",
    marker: "absolute -translate-x-1/2 -translate-y-1/2",
    pill: "absolute rounded-full bg-accent px-2.5 py-1 text-sm font-semibold whitespace-nowrap text-accent-content",
    valueLabel: "absolute text-sm font-semibold whitespace-nowrap text-content",
  },
  variants: {
    isLatest: {
      false: { dot: "size-2 ring-2" },
      true: { dot: "size-4 ring-4" },
    },
    pillX: {
      center: { pill: "left-1/2 -translate-x-1/2" },
      left: { pill: "left-0" },
      right: { pill: "right-0" },
    },
    pillY: {
      above: { pill: "bottom-full mb-8" },
      below: { pill: "top-full mt-2" },
    },
    valueLabelPlacement: {
      above: { valueLabel: "bottom-full left-1/2 mb-1.5 -translate-x-1/2" },
      below: { valueLabel: "top-full left-1/2 mt-1.5 -translate-x-1/2" },
      left: { valueLabel: "top-1/2 right-full mr-1.5 -translate-y-1/2" },
      right: { valueLabel: "top-1/2 left-full ml-1.5 -translate-y-1/2" },
    },
  },
});

const { dot, marker, pill, valueLabel } = plotVariants();

const ScaleBounds = ({ ceilY, floorY }: { ceilY: number; floorY: number }) => {
  return (
    <>
      <line
        className="stroke-line"
        vectorEffect="non-scaling-stroke"
        x1={0}
        x2={100}
        y1={ceilY}
        y2={ceilY}
      />
      <line
        className="stroke-line-strong"
        vectorEffect="non-scaling-stroke"
        x1={0}
        x2={100}
        y1={floorY}
        y2={floorY}
      />
    </>
  );
};

/** Where patients like this one typically get to. The only reference line: there is no projection. */
const IdealLine = ({ span, y }: { span: ProgressChartIdealSpan; y: number }) => {
  return (
    <line
      className="stroke-content-muted"
      strokeDasharray="6 5"
      strokeWidth={1.5}
      vectorEffect="non-scaling-stroke"
      x1={span.startX}
      x2={span.endX}
      y1={y}
      y2={y}
    />
  );
};

const SkippedCheckInMarkers = ({
  floorY,
  markers,
}: {
  floorY: number;
  markers: ProgressChartGeometry["skippedMarkers"];
}) => {
  return markers.map((skipped) => {
    return (
      <line
        className="stroke-line-strong"
        key={skipped.key}
        strokeDasharray="2 2"
        vectorEffect="non-scaling-stroke"
        x1={skipped.x}
        x2={skipped.x}
        y1={floorY}
        y2={floorY + 7}
      />
    );
  });
};

/** Anchored left, and drawn before the dots so a reading is never hidden behind it. */
const IdealValueLabel = ({ score, y }: { score: number; y: number }) => {
  return (
    <span
      className="absolute left-0 -translate-y-1/2 rounded-sm bg-surface px-1 text-sm font-semibold text-content-secondary"
      style={{ top: `${y}%` }}
    >
      {formatProgressScore(score)}
    </span>
  );
};

/** HTML rather than SVG: text inside the stretched SVG would shrink below the 14px floor. */
const PlotMarker = ({
  labelPlacement,
  node,
}: {
  labelPlacement: ProgressChartValueLabelPlacement | undefined;
  node: ProgressChartNode;
}) => {
  const pillPlacement = resolvePillPlacement(node);

  return (
    <div className={marker()} style={{ left: `${node.x}%`, top: `${node.y}%` }}>
      <span className={dot({ isLatest: node.isLatest })} />

      {labelPlacement === undefined ? null : (
        <span className={valueLabel({ valueLabelPlacement: labelPlacement })}>
          {formatProgressScore(node.value)}
        </span>
      )}

      {node.isLatest ? (
        <span className={pill({ pillX: pillPlacement.x, pillY: pillPlacement.y })}>
          You Are Here
        </span>
      ) : null}
    </div>
  );
};

const SkippedCheckInsAnnouncement = ({ count }: { count: number }) => {
  return (
    <p className="sr-only">
      {count === 1
        ? "1 check-in was submitted without answering this question."
        : `${count} check-ins were submitted without answering this question.`}
    </p>
  );
};

export type ProgressChartPlotProps = {
  chart: ProgressChart;
};

/** Every chart is the same blue: meaning lives in the status label, never in the line's color. */
export const ProgressChartPlot = ({ chart }: ProgressChartPlotProps) => {
  const gradientId = useId();
  const plotRef = useRef<HTMLDivElement>(null);
  /* Height matters as much as width: a label clears a neighboring dot on whichever axis it can. */
  const [plotSize, setPlotSize] = useState({ height: 0, width: 0 });

  const geometry = useMemo(() => {
    return buildProgressChartGeometry(chart);
  }, [chart]);

  useEffect(() => {
    const element = plotRef.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      setPlotSize({ height: entry.contentRect.height, width: entry.contentRect.width });
    });

    observer.observe(element);

    return () => {
      return observer.disconnect();
    };
  }, []);

  const description = buildProgressChartDescription(chart);
  const skippedCount = countSkippedCheckIns(chart);
  const hasReadings = geometry.nodes.length > 0;
  const ticks = filterVisibleTicks(geometry.ticks, plotSize.width);
  const valueLabels = resolveValueLabelPlacements(geometry.nodes, plotSize.width, plotSize.height);

  return (
    <div className="flex flex-col gap-2">
      <div
        aria-label={description}
        className="relative h-64 w-full sm:h-72"
        ref={plotRef}
        role="img"
      >
        <svg
          className="absolute inset-0 size-full text-accent"
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
        >
          <defs>
            <linearGradient
              gradientUnits="userSpaceOnUse"
              id={gradientId}
              x1={0}
              x2={0}
              y1={geometry.ceilY}
              y2={geometry.floorY}
            >
              <stop offset="0%" stopColor="currentColor" stopOpacity={0.24} />
              <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
            </linearGradient>
          </defs>

          <ScaleBounds ceilY={geometry.ceilY} floorY={geometry.floorY} />

          {geometry.segments.map((segment) => {
            return segment.areaPath === null ? null : (
              <path d={segment.areaPath} fill={`url(#${gradientId})`} key={`${segment.key}-area`} />
            );
          })}

          {geometry.idealY === null ? null : (
            <IdealLine span={geometry.idealSpan} y={geometry.idealY} />
          )}

          {geometry.segments.map((segment) => {
            return (
              <path
                className="stroke-current"
                d={segment.linePath}
                fill="none"
                key={`${segment.key}-line`}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}

          <SkippedCheckInMarkers floorY={geometry.floorY} markers={geometry.skippedMarkers} />
        </svg>

        {geometry.idealY === null || chart.idealLine === null ? null : (
          <IdealValueLabel score={chart.idealLine.idealScore} y={geometry.idealY} />
        )}

        {geometry.nodes.map((node) => {
          return (
            <PlotMarker key={node.key} labelPlacement={valueLabels.get(node.key)} node={node} />
          );
        })}

        {hasReadings ? null : (
          <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 text-center text-sm text-content-muted">
            {NO_READINGS_MESSAGE}
          </span>
        )}
      </div>

      <div className="relative h-6" aria-hidden>
        {ticks.map((tick) => {
          return (
            <span
              className="absolute -translate-x-1/2 text-sm whitespace-nowrap text-content-muted"
              key={tick.label}
              style={{ left: `${tick.x}%` }}
            >
              {tick.label}
            </span>
          );
        })}
      </div>

      {skippedCount > 0 ? <SkippedCheckInsAnnouncement count={skippedCount} /> : null}
    </div>
  );
};
